import React from "react";
import { getStoredUser } from "../../../../utils/permissions";

export default function UsuariosTableSection({
  loading,
  error,
  filteredUsuarios,
  totalUsuarios,
  canCreate,
  onCreate,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onUnlock,
  unlockingId,
  onRetry,
}) {
  const currentUser = getStoredUser();
  const isCurrentSuperadmin = currentUser?.rol?.nombre === "superadmin";

  return (
    <div className="management-card usuarios-table-card">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
        <div>
          <p className="management-label mb-1">Usuarios registrados</p>
          <h5 className="mb-0">
            {filteredUsuarios.length} resultado{filteredUsuarios.length === 1 ? "" : "s"}
          </h5>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onRetry} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-1" />
          Actualizar
        </button>
      </div>
      <TableContent
        loading={loading}
        error={error}
        filteredUsuarios={filteredUsuarios}
        totalUsuarios={totalUsuarios}
        canCreate={canCreate}
        onCreate={onCreate}
        canEdit={canEdit}
        canDelete={canDelete}
        onEdit={onEdit}
        onDelete={onDelete}
        onUnlock={onUnlock}
        unlockingId={unlockingId}
        onRetry={onRetry}
        isCurrentSuperadmin={isCurrentSuperadmin}
      />
    </div>
  );
}

function TableContent({
  loading,
  error,
  filteredUsuarios,
  totalUsuarios,
  canCreate,
  onCreate,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onUnlock,
  unlockingId,
  onRetry,
  isCurrentSuperadmin,
}) {
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4">
        <p className="text-muted mb-3">{error}</p>
        <button className="btn management-btn-primary" onClick={onRetry}>
          Reintentar
        </button>
      </div>
    );
  }

  if (!filteredUsuarios.length) {
    return <UsuariosEmptyState hasRecords={totalUsuarios > 0} canCreate={canCreate} onCreate={onCreate} />;
  }

  return (
    <UsuariosTable
      usuarios={filteredUsuarios}
      canEdit={canEdit}
      canDelete={canDelete}
      onEdit={onEdit}
      onDelete={onDelete}
      onUnlock={onUnlock}
      unlockingId={unlockingId}
      isCurrentSuperadmin={isCurrentSuperadmin}
    />
  );
}

function UsuariosEmptyState({ hasRecords, canCreate, onCreate }) {
  return (
    <div className="dashboard-empty text-center py-5 usuarios-empty">
      <i className="bi bi-people dashboard-empty-icon usuarios-empty-icon mb-3" />
      <h5 className="fw-semibold mb-1">{hasRecords ? "Sin coincidencias" : "Todavia no hay usuarios"}</h5>
      <p className="mb-3">
        {hasRecords
          ? "Intenta ajustar los filtros para ver otros resultados."
          : "Comienza registrando el primer usuario para verlo aqui."}
      </p>
      {canCreate && (
        <button type="button" className="btn management-btn-primary" onClick={onCreate}>
          Crear usuario
        </button>
      )}
    </div>
  );
}

function UsuariosTable({ usuarios, canEdit, canDelete, onEdit, onDelete, onUnlock, unlockingId, isCurrentSuperadmin }) {
  return (
    <div className="management-table-wrapper table-responsive">
      <table className="table align-middle management-table usuarios-table mb-0">
        <thead>
          <tr>
            <th className="text-center">#</th>
            <th>Nombre</th>
            <th>Documento</th>
            <th>Telefono</th>
            <th>Email</th>
            <th>Rol</th>
            <th>Estado</th>
            <th className="text-end">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {usuarios.map((usuario, idx) => {
            const isTargetSuperadmin = usuario.rol_nombre === "superadmin";
            const isBlocked = !isCurrentSuperadmin && isTargetSuperadmin;
            const editTitle = isBlocked
              ? "Solo otro superadmin puede editar este usuario"
              : "Editar";
            const deleteTitle = isBlocked
              ? "Solo otro superadmin puede eliminar este usuario"
              : "Eliminar";
            const bloqueadoHasta = usuario.bloqueado_hasta ? new Date(usuario.bloqueado_hasta) : null;
            const isLoginBloqueado = !!bloqueadoHasta && bloqueadoHasta > new Date();
            const minutosRestantes = isLoginBloqueado
              ? Math.max(1, Math.ceil((bloqueadoHasta - new Date()) / 60000))
              : 0;
            const isUnlocking = unlockingId === usuario.id;
            return (
              <tr key={usuario.id ?? `usr-${idx}`}>
                <td className="text-center fw-semibold text-muted">{idx + 1}</td>
                <td>
                  <div className="fw-semibold">{usuario.nombre_completo}</div>
                  <small className="text-muted d-block">
                    {usuario.username ? `Usuario: ${usuario.username}` : "Sin usuario"}
                  </small>
                  {/* {usuario.id !== undefined && usuario.id !== null && (
                    <small className="text-muted d-block">ID: {usuario.id}</small>
                  )} */}
                </td>
                <td>{usuario.documento_identidad || "-"}</td>
                <td>{usuario.telefono || "-"}</td>
                <td>{usuario.email || "-"}</td>
                <td>{usuario.rol_nombre || "Sin rol"}</td>
                <td>
                  <div className="d-flex flex-column gap-1">
                    <span className={`badge rounded-pill dashboard-status ${usuario.activo ? "success" : "muted"}`}>
                      {usuario.activo ? "Activo" : "Inactivo"}
                    </span>
                    {isLoginBloqueado && (
                      <span
                        className="badge rounded-pill dashboard-status danger"
                        title="Bloqueado por multiples intentos fallidos de login"
                      >
                        Login bloqueado ({minutosRestantes} min)
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-end">
                  <div className="d-flex justify-content-end gap-2">
                    {isLoginBloqueado && onUnlock && (
                      <button
                        type="button"
                        className="btn btn-icon dashboard-icon-button management-action-btn"
                        onClick={() => onUnlock(usuario)}
                        title="Desbloquear login"
                        disabled={isBlocked || isUnlocking}
                      >
                        <i className={`bi ${isUnlocking ? "bi-hourglass-split" : "bi-unlock"}`} />
                      </button>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        className="btn btn-icon dashboard-icon-button management-action-btn"
                        onClick={() => onEdit(usuario)}
                        title={editTitle}
                        disabled={isBlocked}
                      >
                        <i className="bi bi-pencil-square" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        className="btn btn-icon dashboard-icon-button warning management-action-btn"
                        onClick={() => onDelete(usuario)}
                        title={deleteTitle}
                        disabled={isBlocked}
                      >
                        <i className="bi bi-trash" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
